import { DateTime } from 'luxon';
import { useMemo, useState } from 'react';
import { FormRenderProps } from 'react-final-form';

import MaskedTextField from 'components/formInputs/MaskedTextField';
import { formattedNumberMask } from 'components/formInputs/MaskedTextField/masks';
import RadioGroupField from 'components/formInputs/RadioGroupField';
import SelectField from 'components/formInputs/SelectField';
import { formatOptions } from 'components/formInputs/SelectField/utils';
import TextField from 'components/formInputs/TextField';
import ToggleField from 'components/formInputs/ToggleField';
import TypeAheadField, { getApolloMenuItemsComponent } from 'components/formInputs/TypeAheadField';
import FormWrapper from 'components/FormWrapper';
import ShowHide from 'components/ShowHide';
import { parseInteger } from 'forms/parsers';
import { InitialValues } from 'forms/types';
import useEffectAfterMount from 'hooks/useEffectAfterMount';
import range from 'utils/range';

import {
  CORPORATE_STRUCTURES,
  DISABILITY_INSURANCE_SOURCES,
  EDUCATION_LEVELS,
  INTEND_TO_REPLACE_OPTIONS,
  LABELS,
  OWNERSHIP_STAKES,
  WORK_FROM_HOME_PERCENTAGES,
} from './constants';
import GovernmentEmployeeModal from './GovernmentEmployeeModal';
import { OccupationFormValues } from './types';
import { useSearchJobsQuery } from '../graphql/SearchJobs';

const JobMenuItems = getApolloMenuItemsComponent({
  useQuery: useSearchJobsQuery,
  formatItems: (data) =>
    data?.searchJobs.map((job) => ({
      label: job.title,
      value: job.id,
      subLabel: job.group,
    })) ?? [],
});

const OccupationForm: React.FC<
  FormRenderProps<OccupationFormValues, InitialValues<OccupationFormValues>>
> = ({ handleSubmit, invalid, submitting, values }) => {
  const [showGovernmentEmployeeModal, setShowGovernmentEmployeeModal] = useState(false);
  const showIncomeBeforeCompany = useMemo(() => {
    const currentYear = DateTime.now().year;

    return !!values.companyFoundedAt && values.companyFoundedAt >= currentYear - 1;
  }, [values.companyFoundedAt]);

  const last80Years = useMemo(() => {
    const currentYear = DateTime.now().year;

    return formatOptions(range(currentYear, currentYear - 80, -1));
  }, []);

  useEffectAfterMount(() => {
    if (values.governmentEmployee) {
      setShowGovernmentEmployeeModal(true);
    }
  }, [values.governmentEmployee]);

  return (
    <>
      <FormWrapper handleSubmit={handleSubmit} invalid={invalid} submitting={submitting}>
        <TypeAheadField name="job" label={LABELS.job} menuItemsComponent={JobMenuItems} />
        <ToggleField name="atLeast30HoursAWeek" groupLabel={LABELS.atLeast30HoursAWeek} />
        <TextField
          name="yearsOfExperience"
          label={LABELS.yearsOfExperience}
          config={{ parse: parseInteger }}
          inputMode="numeric"
        />
        <ToggleField name="governmentEmployee" groupLabel={LABELS.governmentEmployee} />
        <SelectField<number>
          name="workFromHomePercentage"
          label={LABELS.workFromHomePercentage}
          options={WORK_FROM_HOME_PERCENTAGES}
          config={{ parse: parseInteger }}
        />
        <ToggleField name="selfEmployed" groupLabel={LABELS.selfEmployed} />
        <ShowHide visible={!!values.selfEmployed}>
          <SelectField
            name="corporateStructure"
            label={LABELS.corporateStructure}
            options={CORPORATE_STRUCTURES}
          />
          <SelectField
            name="ownershipStake"
            label={LABELS.ownershipStake}
            options={OWNERSHIP_STAKES}
          />
          <TextField
            name="numberOfEmployees"
            label={LABELS.numberOfEmployees}
            config={{ parse: parseInteger }}
            inputMode="numeric"
          />
          <TextField
            name="numberOfPartners"
            label={LABELS.numberOfPartners}
            config={{ parse: parseInteger }}
            inputMode="numeric"
          />
          <SelectField
            name="companyFoundedAt"
            label={LABELS.companyFoundedAt}
            options={last80Years}
            config={{ parse: parseInteger }}
          />
          <ShowHide visible={showIncomeBeforeCompany}>
            <MaskedTextField
              config={{ parse: parseInteger }}
              inputMode="numeric"
              label={LABELS.annualIncomeBeforeCompany}
              maskOptions={formattedNumberMask}
              name="annualIncomeBeforeCompany"
              prefix="$"
            />
          </ShowHide>
          <SelectField
            name="becameOwnerAt"
            label={LABELS.becameOwnerAt}
            options={last80Years}
            config={{ parse: parseInteger }}
          />
        </ShowHide>
        <SelectField
          name="educationLevel"
          label={LABELS.educationLevel}
          options={EDUCATION_LEVELS}
        />
        <MaskedTextField
          config={{ parse: parseInteger }}
          inputMode="numeric"
          label={LABELS.individualIncome}
          maskOptions={formattedNumberMask}
          name="individualIncome"
          prefix="$"
        />
        <ToggleField
          name="disabilityInsuranceStatus"
          groupLabel={LABELS.disabilityInsuranceStatus}
        />
        <ShowHide visible={!!values.disabilityInsuranceStatus}>
          <MaskedTextField
            config={{ parse: parseInteger }}
            inputMode="numeric"
            label={LABELS.existingCoverage}
            maskOptions={formattedNumberMask}
            name="existingCoverage"
            prefix="$"
          />
          <SelectField
            name="disabilityInsuranceSource"
            label={LABELS.disabilityInsuranceSource}
            options={DISABILITY_INSURANCE_SOURCES}
          />
          <ShowHide visible={values.disabilityInsuranceSource === 'employer'}>
            <ToggleField name="employerPaysPremium" groupLabel={LABELS.employerPaysPremium} />
          </ShowHide>
          <RadioGroupField
            name="intendToReplace"
            groupLabel={LABELS.intendToReplace}
            options={INTEND_TO_REPLACE_OPTIONS}
            helperText="If you are replacing an existing policy, note that your new policy will probably be more expensive than your previous one. As you get older, even by just a few years, premiums for otherwise identical policies increase."
            config={{
              parse: (v: string) => JSON.parse(v),
              format: (v: string) => JSON.stringify(v),
            }}
          />
        </ShowHide>
      </FormWrapper>
      <GovernmentEmployeeModal
        showModal={showGovernmentEmployeeModal}
        setShowModal={setShowGovernmentEmployeeModal}
      />
    </>
  );
};

export default OccupationForm;
